import { useCallback, useMemo } from "react";
import { hapticsCapacitorAdapter } from "../adapters/capacitor/haptics.capacitor";
import { hapticsWebAdapter } from "../adapters/web/haptics.web";
import { isCapacitorRuntime } from "../lib/is-capacitor-runtime";
import type { HapticImpactStyle, HapticNotificationType, HapticsPort } from "../ports/haptics.port";

// Единый haptic API.
//
// Адаптер выбирается В РАНТАЙМЕ, как у остальных портов проекта
// (audio-offline-cache / image-offline-cache / use-tutor-stt / network).
// Раньше дефолтом стоял web-адаптер с комментарием «Capacitor-сборка
// перехватывает через alias» — такого алиаса в apps/web/vite.config.ts нет и не
// было, а `resolve.conditions` из rules/capacitor-platform.md в проекте не
// применяется. Итог: в WKWebView (iOS) `navigator.vibrate` отсутствует →
// тактильной отдачи не было вовсе, на Android была грубая вибрация вместо
// нативных Taptic/Composition-паттернов.
//
// Оба адаптера импортируются статически: capacitor-версия ходит через
// canonical `window.Capacitor.Plugins.*` и не тянет @capacitor/* в граф,
// поэтому web-бандлу это ничего не стоит.
export interface HapticController {
	impact(opts: { style: HapticImpactStyle }): void;
	selection(): void;
	notification(opts: { type: HapticNotificationType }): void;
}

/** Платформенный адаптер по умолчанию. Явный `adapter` в тестах перекрывает. */
function defaultHapticsAdapter(): HapticsPort {
	return isCapacitorRuntime() ? hapticsCapacitorAdapter : hapticsWebAdapter;
}

export function useHaptic(adapter: HapticsPort = defaultHapticsAdapter()): HapticController {
	const impact = useCallback(
		(opts: { style: HapticImpactStyle }) => {
			void adapter.impact(opts);
		},
		[adapter],
	);
	const selection = useCallback(() => {
		void adapter.selection();
	}, [adapter]);
	const notification = useCallback(
		(opts: { type: HapticNotificationType }) => {
			void adapter.notification(opts);
		},
		[adapter],
	);

	return useMemo(() => ({ impact, selection, notification }), [impact, selection, notification]);
}
